/**
 * Where a visitor came from, kept in their own browser until they decide.
 *
 * Analytics only starts once the cookie banner is accepted, so everything
 * before that moment was never recorded: the first paying Mexican customer
 * arrived, signed up, and paid in half an hour, and PostHog's first event for
 * them is `account_created`. No landing page, no referrer, no utm — the
 * acquisition side was blind for everyone who accepts late, which is most
 * people.
 *
 * So the first load writes the entry URL and referrer to this browser and
 * nothing else happens. **Nothing is sent.** If they accept, it becomes person
 * properties; if they decline, it is deleted. Nothing leaves before analytics
 * is on: after the bar is accepted in the EU/EEA, the UK and Switzerland, at
 * once elsewhere (utils/consentRegion.js).
 *
 * localStorage, not sessionStorage: signing up with Google leaves our origin
 * for accounts.google.com and comes back, and a stash that does not survive
 * that round trip would rebuild the same blind spot one step later. It is also
 * where the consent answer itself already lives.
 */
const KEY = 'cs_first_touch';
const SENT_KEY = 'cs_first_touch_sent';
// A landing older than this is not what brought them to today's signup
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    // Private mode, blocked storage: attribution is not worth an exception
    return null;
  }
};

/** What this browser remembers, or null when there is nothing fresh. */
export function readFirstTouch(now = Date.now()) {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(KEY);
    if (!raw) return null;
    const touch = JSON.parse(raw);
    if (!touch?.at || now - Date.parse(touch.at) > MAX_AGE_MS) {
      store.removeItem(KEY);
      store.removeItem(SENT_KEY);
      return null;
    }
    return touch;
  } catch {
    return null;
  }
}

/**
 * Called on the first load of a visit, before any consent exists. Writes to
 * this browser only. The first landing wins: someone who arrives on a legend
 * page and wanders to /ai-agent before signing up was still brought in by the
 * legend page.
 */
export function rememberFirstTouch(now = Date.now()) {
  const store = storage();
  if (!store) return null;
  const existing = readFirstTouch(now);
  if (existing) return existing;
  try {
    const touch = {
      // The whole URL, so utm_* and any campaign parameters survive
      url: window.location.href,
      referrer: document.referrer || null,
      at: new Date(now).toISOString(),
    };
    store.setItem(KEY, JSON.stringify(touch));
    return touch;
  } catch {
    return null;
  }
}

/** Declined: the stash is deleted, not held. */
export function forgetFirstTouch() {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(KEY);
    store.removeItem(SENT_KEY);
  } catch {
    // Nothing to do; it was never sent anywhere
  }
}

/** The properties a first touch becomes, with the campaign parameters parsed out. */
export function firstTouchProperties(touch) {
  if (!touch?.url) return null;
  const properties = {
    first_touch_url: touch.url,
    first_touch_referrer: touch.referrer || null,
    first_touch_at: touch.at,
  };
  try {
    const url = new URL(touch.url);
    properties.first_touch_path = url.pathname;
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const value = url.searchParams.get(key);
      if (value) properties[`first_touch_${key}`] = value;
    }
    if (touch.referrer) {
      properties.first_touch_referring_domain = new URL(touch.referrer).hostname;
    }
  } catch {
    // A URL we cannot parse still tells us where they landed
  }
  return properties;
}

/**
 * Accepted: hand the stash over, once. Returns what was sent, or null when
 * there is nothing to send or it has already gone.
 */
export function sendFirstTouch(posthog, now = Date.now()) {
  const store = storage();
  const touch = readFirstTouch(now);
  const properties = firstTouchProperties(touch);
  if (!properties) return null;
  try {
    if (store?.getItem(SENT_KEY)) return null;
  } catch {
    // No storage: better to send twice than never
  }
  try {
    // setPersonProperties puts it on the person, so it survives into every
    // later session and can be grouped by in an insight
    posthog.setPersonProperties(properties);
    posthog.capture('first_touch_recovered', properties);
    store?.setItem(SENT_KEY, '1');
    return properties;
  } catch {
    return null;
  }
}
